import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import {
  Overlay,
  Container,
  Header,
  Title,
  ListItems,
  ListItem,
  ListItemTitle,
  ListItemParagraph,
  BoldText,
  NestedLists,
  NestedList,
} from './IntenalProductMoreInfoDialog.styles';

interface IIntenalProductMoreInfoDialog {
  open: boolean;
  onClose: () => void;
}

export const IntenalProductMoreInfoDialog = ({
  open,
  onClose,
}: IIntenalProductMoreInfoDialog) => {
  const portalDiv = document.getElementById('portal');

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <Title>Identification Status</Title>
              <BlueIcon icon="cross" color="#8F95B2" onClick={onClose} />
            </Header>
            <ListItems>
              <ListItem>
                <ListItemTitle>Identified</ListItemTitle>
                <ListItemParagraph>
                  These are products that have been validated{' '}
                  <i>
                    <BoldText>and</BoldText>
                  </i>{' '}
                  matched to a real product. Identification is important as{' '}
                  <BoldText>
                    only identified products are augmented with data
                  </BoldText>{' '}
                  (updates, documents, specifications, end of life alerts,
                  warranty, and more).
                </ListItemParagraph>
              </ListItem>
              <ListItem>
                <ListItemTitle>Unidentified</ListItemTitle>
                <ListItemParagraph>
                  Beam depends on the data you shared with us to match products.
                  For a product to be unidentified, one of two scenarios
                  occurred:
                </ListItemParagraph>
                <NestedLists>
                  <NestedList>
                    Your data is correct, but we do not have that product yet
                  </NestedList>
                  <NestedList>
                    Your data is inaccurate, and with it our automated matching
                    process was not able to make a match. Here you have a few
                    options:
                    <NestedLists>
                      <NestedList>
                        Change your information
                        <NestedLists>
                          <NestedList>
                            In Onboarding, you we provide warnings on the cells.
                            While seeing the warning details you can change the
                            original input, or even select one of the similar
                            products we have found from the information you
                            shared.
                          </NestedList>
                        </NestedLists>
                      </NestedList>
                      <NestedList>
                        Request Identification{' '}
                        <NestedLists>
                          <NestedList>
                            Whether you are confident about the data you shared,
                            have a critical asset that needs matching, or need
                            our help to find the data for you. You can always
                            use the identification request (see more below)
                          </NestedList>
                        </NestedLists>
                      </NestedList>
                      <NestedList>
                        Mark it as an Internal (see more below)
                      </NestedList>
                    </NestedLists>
                  </NestedList>
                </NestedLists>
              </ListItem>
              <ListItem>
                <ListItemTitle>Internal Products</ListItemTitle>
                <ListItemParagraph>
                  These are products for products where matching is not
                  possible, or not needed:
                </ListItemParagraph>
                <ListItems data-list-type="disc">
                  <ListItem>
                    Custom products
                    <ListItems data-list-type="disc">
                      <ListItem>
                        These would be products not available for sale in the
                        market or that have been custom manufactured for your
                        needs. These cannot be matched as data for them is not
                        publicly available.
                      </ListItem>
                    </ListItems>
                  </ListItem>
                  <ListItem>
                    Accessories
                    <ListItems data-list-type="disc">
                      <ListItem>
                        After Beam is unable to match your product, we process
                        the information you shared to mark it as a accessory if
                        possible. For example, if a product has “charger” in the
                        provided name, we will mark it as a potential internal
                        product. Other examples: “cable”, “adapter”, “strap”,
                        “office depot”
                      </ListItem>
                    </ListItems>
                  </ListItem>
                  <ListItem>
                    By choosing to mark it as internal yourself
                    <ListItems data-list-type="disc">
                      <ListItem>
                        This is a way for you to tell us “I do not need a match
                        for this product”. For example, you may have a “desk” in
                        your list. Beam only provides data for Broadcast
                        equipment (and in the future IT equipment) so this is a
                        product where a match will not be ever made. Marking as
                        internal allows you and us to more clearly see what
                        still requires matching in your list.
                      </ListItem>
                    </ListItems>
                  </ListItem>
                </ListItems>
              </ListItem>
              <ListItem>
                <ListItemTitle>Identification Requests</ListItemTitle>
                <ListItemParagraph>
                  When an identification requests is opened, we get notified and
                  manually verify your information to try and find your product.
                  We will often need your support by sharing more details on
                  your product if needed or to confirm one or more products that
                  we found. And select the appropriate one. An identification
                  request should result in a match, or a match deemed impossible
                  due to lack of data or the nature of the product becoming
                  clearer.
                </ListItemParagraph>
              </ListItem>
            </ListItems>
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
